import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Cart Context
const CartContext = createContext();

// Cart Actions
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART',
  SET_CART_OPEN: 'SET_CART_OPEN'
};

// Initial Cart State
const initialState = {
  items: [],
  isOpen: false,
  totalItems: 0,
  subtotal: 0
};

// Cart Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const { product, quantity = 1, metal_type = null } = action.payload;
      const productId = product._id || product.id;
      
      // Check if item already exists with same metal type
      const existingItemIndex = state.items.findIndex(
        item => (item.product._id || item.product.id) === productId && item.metal_type === metal_type
      );
      
      let newItems;
      if (existingItemIndex >= 0) {
        // Update existing item quantity
        newItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item
        newItems = [...state.items, { 
          product: { ...product, _id: productId }, 
          quantity,
          metal_type
        }];
      }
      
      return {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        subtotal: newItems.reduce((sum, item) => {
          const price = parseFloat(item.product.price || item.product.regular_price || 0);
          return sum + (price * item.quantity);
        }, 0),
        isOpen: true // Auto-open cart when item is added
      };
    }
    
    case CART_ACTIONS.REMOVE_ITEM: {
      const { productId } = action.payload;
      const newItems = state.items.filter(
        item => (item.product._id || item.product.id) !== productId
      );
      
      return {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        subtotal: newItems.reduce((sum, item) => {
          const price = parseFloat(item.product.price || item.product.regular_price || 0);
          return sum + (price * item.quantity);
        }, 0)
      };
    }
    
    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { productId, quantity } = action.payload;
      
      if (quantity <= 0) {
        // Remove item if quantity is 0 or less
        return cartReducer(state, { 
          type: CART_ACTIONS.REMOVE_ITEM, 
          payload: { productId } 
        });
      }
      
      const newItems = state.items.map(item => {
        const itemId = item.product._id || item.product.id;
        return itemId === productId
          ? { ...item, quantity }
          : item;
      });
      
      return {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        subtotal: newItems.reduce((sum, item) => {
          const price = parseFloat(item.product.price || item.product.regular_price || 0);
          return sum + (price * item.quantity);
        }, 0)
      };
    }
    
    case CART_ACTIONS.CLEAR_CART: {
      return {
        ...state,
        items: [],
        totalItems: 0,
        subtotal: 0
      };
    }
    
    case CART_ACTIONS.LOAD_CART: {
      const { items } = action.payload;
      return {
        ...state,
        items: items || [],
        totalItems: (items || []).reduce((sum, item) => sum + item.quantity, 0),
        subtotal: (items || []).reduce((sum, item) => {
          const price = parseFloat(item.product.price || item.product.regular_price || 0);
          return sum + (price * item.quantity);
        }, 0)
      };
    }
    
    case CART_ACTIONS.SET_CART_OPEN: {
      return {
        ...state,
        isOpen: action.payload
      };
    }
    
    default:
      return state;
  }
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState);
  const [shippingFee, setShippingFee] = React.useState(10.00); // Default fallback

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('umoonaOpalCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({ 
          type: CART_ACTIONS.LOAD_CART, 
          payload: { items: parsedCart.items || [] } 
        });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem('umoonaOpalCart');
      }
    }
  }, []);

  // Fetch shipping settings from backend
  useEffect(() => {
    const fetchShippingSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping/settings`);
        if (response.ok) {
          const data = await response.json();
          setShippingFee(data.shipping_fee || 10.00);
        }
      } catch (error) {
        console.error('Error fetching shipping settings:', error);
        // Keep default shipping fee on error
      }
    };
    
    fetchShippingSettings();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('umoonaOpalCart', JSON.stringify({
      items: cart.items,
      timestamp: Date.now()
    }));
  }, [cart.items]);

  // Cart Actions
  const addToCart = (product, quantity = 1, metal_type = null) => {
    dispatch({ 
      type: CART_ACTIONS.ADD_ITEM, 
      payload: { product, quantity, metal_type } 
    });
  };

  const removeFromCart = (productId) => {
    dispatch({ 
      type: CART_ACTIONS.REMOVE_ITEM, 
      payload: { productId } 
    });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ 
      type: CART_ACTIONS.UPDATE_QUANTITY, 
      payload: { productId, quantity } 
    });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  const setCartOpen = (isOpen) => {
    dispatch({ 
      type: CART_ACTIONS.SET_CART_OPEN, 
      payload: isOpen 
    });
  };

  // Helper functions
  const getItemPrice = (product) => {
    return parseFloat(product.sale_price || product.price || product.regular_price || 0);
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price || 0);
    return numPrice > 0 ? `$${numPrice.toFixed(2)}` : 'Call for Price';
  };

  // Shipping is a flat fee per order, so an order only ships free when EVERY
  // item in it is flagged no_shipping_fee. A mixed cart pays the normal fee.
  // The `=== true` is deliberate: carts rehydrated from localStorage were saved
  // before this field existed, so the value is undefined and must charge.
  const allItemsShipFree = React.useMemo(() => {
    const list = cart.items || [];
    if (list.length === 0) return false; // an empty cart makes no free-shipping claim
    return list.every((item) => item?.product?.no_shipping_fee === true);
  }, [cart.items]);

  const getShippingFee = () => {
    return allItemsShipFree ? 0 : shippingFee;
  };

  const getTotal = () => {
    return cart.subtotal + getShippingFee();
  };

  // Single source for the shipping sentence shown around the store. Note that
  // formatPrice(0) returns 'Call for Price', so the free case must not go
  // through it.
  const getShippingNote = () => {
    return getShippingFee() === 0
      ? 'Free shipping on this order to all Australian locations'
      : `${formatPrice(getShippingFee())} flat rate shipping to all Australian locations`;
  };

  const value = {
    // Cart state
    cart,
    items: cart.items,
    totalItems: cart.totalItems,
    subtotal: cart.subtotal,
    isCartOpen: cart.isOpen,
    
    // Cart actions
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    setCartOpen,
    
    // Helper functions
    getItemPrice,
    formatPrice,
    getShippingFee,
    getShippingNote,
    getTotal
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;